// src/components/EvaluationModal.js
import React, { useState } from "react";
import { CloseIcon } from "../../assets/icons/icons";
import { supabase } from "../../supabaseClient";
import { useConfig } from "../../contexts/ConfigContext";
import "./style.css";

// label → numeric category mapping
const categoryLabelToId = {
  "Safety & Wellness": 1,
  Development: 2,
  Culture: 3,
};

export const categoryOptions = [
  { value: "Safety & Wellness", optionLabel: "Safety & Wellness" },
  { value: "Development",       optionLabel: "Development" },
  { value: "Culture",           optionLabel: "Culture" },
];

function EvaluationCreation({ closeModal }) {
  const [evaluationName, setEvaluationName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [numParticipants, setNumParticipants] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [evaluationCreated, setEvaluationCreated] = useState(false);
  const [evaluationUrl, setEvaluationUrl] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [error, setError] = useState("");
  const API_BASE = useConfig().config.apiBase;
  const today = new Date().toISOString().split("T")[0];

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setSelectedCategories((cats) =>
      checked ? [...cats, value] : cats.filter((v) => v !== value)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setValidationErrors({});

    let errors = {};
    if (!evaluationName.trim()) {
      errors.evaluationName = "Evaluation Name is required";
    }
    if (!startDate) {
      errors.startDate = "Start Date is required";
    }
    if (!endDate) {
      errors.endDate = "End Date is required";
    }
    if (startDate && endDate && endDate < startDate) {
      errors.endDate = "End Date must be after or equal to Start Date";
    }
    if (!numParticipants) {
      errors.numParticipants = "Number of Participants is required";
    } else if (parseInt(numParticipants, 10) < 1) {
      errors.numParticipants = "Number of Participants must be at least 1";
    }
    if (selectedCategories.length === 0) {
      errors.selectedCategories = "Please select at least one category";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    // console.log("About to fetch user:", await supabase.auth.getSession());

    // 1️⃣ get logged‑in user session
       const {
          data: { session },
        } = await supabase.auth.getSession();
        if (error || !session) {
          console.error("Session expired, please login again");
          return;
        }
        const token = session?.access_token;
    const managerId = session.user.id;
   

    // 2️⃣ build the request payload
    const payload = {
      evaluationName,
      startDate,
      endDate,
      numberOfParticipants: parseInt(numParticipants, 10),
      selectedCategories: selectedCategories.map((lbl) => categoryLabelToId[lbl]),
      managerId,
    };

    try {
      const res = await fetch(`${API_BASE}/evaluation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // Handle 401 Unauthorized - Token expired or invalid
      if (res.status === 401) {
        console.warn("🔐 Token expired or invalid");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userPermissions");
        window.location.href = "/login";
        return;
      }

      // parse raw text so any HTML error pages surface
      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error(text);
      }
      // console.log("🛠️ createEvaluation response JSON:", json);
      // temporarily disable ID guard while debugging:
      // if (!json.id) throw new Error("Server didn’t return the new evaluation ID.");
      if (!res.ok) throw new Error(json.error || res.statusText);

      // success!
      setEvaluationUrl(json.evaluationUrl);
      setEmailTemplate(json.emailTemplate);
      setEvaluationCreated(true);
    } catch (err) {
      // console.error("Create evaluation error", err);
      setError(err.message.replace(/</g, "&lt;")); // sanitize any HTML
    }
  };

  // — Form screen —
  if (!evaluationCreated) {
    return (
      <div className="evaluation-creation-container">
        <div className="modal-header">
          <h1>Create New Evaluation</h1>
          <CloseIcon onClick={closeModal} className="cancel-icon" />
        </div>

        {error && <p className="error-text">{error}</p>}

        <form onSubmit={handleSubmit} className="evaluation-creation-form" noValidate>
          <div className="form-group">
            <label className="entry-title">Evaluation Name:</label>
            <input
              type="text"
              value={evaluationName}
              onChange={(e) => {
                setEvaluationName(e.target.value);
                if (validationErrors.evaluationName) {
                  setValidationErrors(p => ({ ...p, evaluationName: null }));
                }
              }}
              placeholder="Evaluation Name"
              className={`evaluation-input ${validationErrors.evaluationName ? 'error' : ''}`}
            />
            {validationErrors.evaluationName && <div className="validation-error">{validationErrors.evaluationName}</div>}
          </div>

          <div className="form-group">
            <label className="entry-title">Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (validationErrors.startDate) {
                  setValidationErrors(p => ({ ...p, startDate: null }));
                }
              }}
              min={today}
              className={`evaluation-input ${validationErrors.startDate ? 'error' : ''}`}
            />
            {validationErrors.startDate && <div className="validation-error">{validationErrors.startDate}</div>}
          </div>

          <div className="form-group">
            <label className="entry-title">End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (validationErrors.endDate) {
                  setValidationErrors(p => ({ ...p, endDate: null }));
                }
              }}
              min={today}
              className={`evaluation-input ${validationErrors.endDate ? 'error' : ''}`}
            />
            {validationErrors.endDate && <div className="validation-error">{validationErrors.endDate}</div>}
          </div>

          <div className="form-group">
            <label className="entry-title">Number of Participants:</label>
            <input
              type="number"
              min="1"
              value={numParticipants}
              onChange={(e) => {
                setNumParticipants(e.target.value);
                if (validationErrors.numParticipants) {
                  setValidationErrors(p => ({ ...p, numParticipants: null }));
                }
              }}
              placeholder="Number of Participants"
              className={`evaluation-input ${validationErrors.numParticipants ? 'error' : ''}`}
            />
            {validationErrors.numParticipants && <div className="validation-error">{validationErrors.numParticipants}</div>}
          </div>

          <div className="form-group checkbox-container">
            <label className="entry-title">Select Categories:</label>
            <div className="checkbox-input-container">
              {categoryOptions.map((opt) => (
                <div key={opt.value} className="category-container">
                  <input
                    type="checkbox"
                    id={`cat-${opt.value}`}
                    value={opt.value}
                    checked={selectedCategories.includes(opt.value)}
                    onChange={(e) => {
                      handleCategoryChange(e);
                      if (validationErrors.selectedCategories) {
                        setValidationErrors(p => ({ ...p, selectedCategories: null }));
                      }
                    }}
                    className="category-input"
                  />
                  <label htmlFor={`cat-${opt.value}`} className="category-label">
                    {opt.optionLabel}
                  </label>
                </div>
              ))}
            </div>
            {validationErrors.selectedCategories && <div className="validation-error">{validationErrors.selectedCategories}</div>}
          </div>

          <button type="submit" className="evaluation-submit-btn">
            Create Evaluation
          </button>
        </form>
      </div>
    );
  }

  // — Success screen —
  return (
    <div className="modal-overlay-fullscreen">
      <div className="modal-content-fullscreen">
        <div className="modal-header-fullscreen">
          <h2>Your evaluation has been created!</h2>
          <CloseIcon onClick={closeModal} className="cancel-icon" />
        </div>
        <br></br>
        <p>Please copy and paste this evaluation URL when sending an email to your athletes or copy our
        recommended email content below.</p>
        <div className="copy-section">
          <label className="evaluation-label">Copy URL</label>
          <div className="copy-box">
            <input
              type="text"
              readOnly
              value={evaluationUrl}
              className="copy-input"
              onClick={() => navigator.clipboard.writeText(evaluationUrl)}
            />
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(evaluationUrl)}
              className="copy-button"
            >
              Copy
            </button>
          </div>
        </div>

        <p>Or copy this recommended email content:</p>
        <div className="copy-section">
          <label className="evaluation-label">Recommended Email Content</label>
          <textarea
            readOnly
            value={emailTemplate}
            className="copy-textarea"
            onClick={() => navigator.clipboard.writeText(emailTemplate)}
          />
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(emailTemplate)}
            className="copy-button"
          >
            Copy Email
          </button>
        </div>

        <p>
          Check your evaluation dashboard after the evaluation completion date to see results.
        </p>
        <br></br>
        <p>Thank you from the SWP team.</p>
      </div>
    </div>
  );
}

export default function ReflectionModal({ isOpen, closeModal }) {
  if (!isOpen) return null;
  return (
    <div className="evaluation-modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <EvaluationCreation closeModal={closeModal} />
      </div>
    </div>
  );
}
